import { useEffect } from 'react';

import { useAtom } from 'jotai';
import { PaginationForSearch } from 'ui';

import { useTableContext } from '../table.context';

type TablePaginationProps = {
  /**
   * This was externalized as a prop rather than using it in <Table /> to conditionally render
   * the <TablePagination /> component.
   *
   * This was done to allow the <TablePagination /> component to retain the
   * searchObject<->pagination behavior although the component might not be rendered.
   */
  withPagination?: boolean;
};

const TablePagination = ({ withPagination = true }: TablePaginationProps) => {
  const ctx = useTableContext();
  const [searchObject] = useAtom(ctx.tableSearchObjectAtom, {
    store: ctx.atomStore,
  });

  // 1 index value to align with api pagination
  const paginationValue = ctx.page + 1;
  const isUnknownTotal = ctx?.pageCount === -1;
  // IF we  know it's a deliberate unknown, we can just + 1 to the pagination value for the total so we have an extra page: Otherwise lets just use the page count we were giving
  const paginationTotal = isUnknownTotal ? paginationValue + 1 : ctx?.pageCount;

  // Reset to the first page when the search changes
  useEffect(() => {
    ctx.table.setPageIndex(0);
  }, [searchObject.searches]);

  if (!withPagination) {
    return null;
  }

  return (
    <PaginationForSearch
      selectProps={{
        defaultValue: String(ctx.table.getState().pagination.pageSize),
        onChange(value) {
          ctx.table.setPageSize(Number(value));
        },
      }}
      paginationProps={{
        total: paginationTotal,
        value: paginationValue,
        onChange(number) {
          const page = number ? number : 0;
          ctx.table.setPageIndex(page - 1);
        },
        isTotalUnknown: isUnknownTotal,
      }}
    />
  );
};

export { TablePagination };
